import type { ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

const POINTER_OFFSET_X = 12;
const POINTER_OFFSET_Y = 12;
const LIST_HOVER_TOOLTIP_CONTENT_CLASSNAME =
  'rounded-sm min-w-[250px] max-w-[min(100vw-1rem,360px)] whitespace-normal text-xs p-2 bg-[#FAFAFA] text-DarkGrey shadow-lg';
const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

/** Shifts the tooltip so it stays on screen, a few pixels away from the edges. */
export const clampTooltip = (
  left: number,
  top: number,
  tooltipWidth: number,
  tooltipHeight: number,
  viewportWidth: number,
  viewportHeight: number,
  margin = 8
): { left: number; top: number } => {
  const maxLeft = viewportWidth - margin - tooltipWidth;
  const maxTop = viewportHeight - margin - tooltipHeight;
  return {
    left: clamp(left, margin, Math.max(margin, maxLeft)),
    top: clamp(top, margin, Math.max(margin, maxTop)),
  };
};

export type MouseFollowTooltipProps = {
  children: ReactNode;
  className?: string;
  clientX: number;
  clientY: number;
  isOpen: boolean;
  zIndex?: number;
};

const MouseFollowTooltip = ({
  children,
  className,
  clientX,
  clientY,
  isOpen,
  zIndex = 50,
}: MouseFollowTooltipProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [clampedPosition, setClampedPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setClampedPosition(null);
      return;
    }
    if (!contentRef.current || typeof window === 'undefined') return;
    const rect = contentRef.current.getBoundingClientRect();
    const rawLeft = clientX + POINTER_OFFSET_X;
    const rawTop = clientY + POINTER_OFFSET_Y;
    setClampedPosition(
      clampTooltip(
        rawLeft,
        rawTop,
        rect.width,
        rect.height,
        window.innerWidth,
        window.innerHeight
      )
    );
  }, [isOpen, clientX, clientY, children]);

  if (!isOpen || typeof document === 'undefined') return null;

  const left = clampedPosition?.left ?? clientX + POINTER_OFFSET_X;
  const top = clampedPosition?.top ?? clientY + POINTER_OFFSET_Y;

  return createPortal(
    <div
      ref={contentRef}
      data-testid="custom-tooltip"
      className={twMerge('pointer-events-none fixed', LIST_HOVER_TOOLTIP_CONTENT_CLASSNAME, className)}
      style={{ left, top, zIndex }}
    >
      {children}
    </div>,
    document.body
  );
};

export default MouseFollowTooltip;
